/**
 * Meta Pixel (client-side) — F1.4.
 * Carga fbevents.js y expone helpers. Lee el Pixel ID de VITE_META_PIXEL_ID.
 * Si no está configurado, todo es no-op (dev sin pixel no rompe nada).
 *
 * Dedup con CAPI: cada evento Lead se dispara con un eventID que TAMBIÉN se manda
 * al webhook para que el server (CAPI) reporte el mismo event_id → Meta deduplica.
 */
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

let initialized = false;

export function initPixel(): void {
  if (initialized || !PIXEL_ID || typeof window === "undefined") return;
  initialized = true;

  /* eslint-disable */
  (function (f: any, b, e, v, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  window.fbq?.("init", PIXEL_ID);
  window.fbq?.("track", "PageView");
}

type Params = Record<string, unknown>;

/** Evento estándar de Meta (ViewContent, Lead, InitiateCheckout, Schedule…). */
export function track(event: string, params: Params = {}, eventId?: string): void {
  if (!PIXEL_ID || typeof window === "undefined" || !window.fbq) return;
  window.fbq("track", event, params, eventId ? { eventID: eventId } : undefined);
}

/** Evento custom (LeadFormStarted) — no estándar, pero sirve para audiencias/retargeting. */
export function trackCustom(event: string, params: Params = {}, eventId?: string): void {
  if (!PIXEL_ID || typeof window === "undefined" || !window.fbq) return;
  window.fbq("trackCustom", event, params, eventId ? { eventID: eventId } : undefined);
}

/* ── Embudo de leads (los 4 niveles) ──────────────────────────────────────── */

/** COLD — llegó del ad y vio la página (aún sin interactuar). */
export function trackViewContent(contentName: string): void {
  track("ViewContent", { content_name: contentName, content_category: "bathroom" });
}

/** WARM — empezó a llenar el form/estimador pero no lo completó. */
export function trackLeadFormStarted(leadSource: string): void {
  trackCustom("LeadFormStarted", { lead_source: leadSource });
}

/** Lead capturado — completó el form de contacto (dedup con CAPI vía eventId). */
export function trackLead(eventId: string, value?: number): void {
  track("Lead", value ? { currency: "USD", value } : {}, eventId);
}

/** HOT — llegó al paso de agendar (calendario a la vista) pero puede irse sin agendar. */
export function trackReachedBooking(eventId?: string, value?: number): void {
  track("InitiateCheckout", value ? { currency: "USD", value } : {}, eventId);
}

/** REAL — agendó la cita en el calendario. */
export function trackSchedule(eventId?: string, value?: number): void {
  track("Schedule", value ? { currency: "USD", value } : {}, eventId);
}

/** Lee una cookie por nombre (para _fbp / _fbc que enriquecen el match de CAPI). */
export function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : undefined;
}
